import { useState } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useCoachNotificationItems } from "@/hooks/useCoachNotificationItems";
import type { CoachNotificationItem, NotificationSeverity } from "@/hooks/useCoachNotificationItems";

// ── Severity dot ──────────────────────────────────────────────────────────────

function severityDotClass(severity: NotificationSeverity): string {
  switch (severity) {
    case "high":
      return "bg-amber-500";
    case "medium":
      return "bg-blue-500";
    case "low":
    default:
      return "bg-muted-foreground/40";
  }
}

// ── Single notification row ───────────────────────────────────────────────────

interface NotificationRowProps {
  item: CoachNotificationItem;
  onSelect: (item: CoachNotificationItem) => void;
}

function NotificationRow({ item, onSelect }: NotificationRowProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className={cn(
        "w-full text-left px-3 py-2.5 rounded-md transition-colors hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        !item.read && "bg-accent/30",
      )}
    >
      <div className="flex items-start gap-2">
        <span
          className={cn(
            "mt-1.5 h-2 w-2 rounded-full shrink-0",
            severityDotClass(item.severity),
            item.read && "opacity-30",
          )}
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              "text-sm font-medium leading-snug truncate",
              item.read && "text-muted-foreground",
            )}
          >
            {item.title}
          </p>
          <p className="text-xs text-muted-foreground leading-snug mt-0.5 line-clamp-1">
            {item.description}
          </p>
          {item.createdAt && (
            <p className="text-[10px] text-muted-foreground/60 mt-0.5">
              {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export interface CoachNotificationBellProps {
  /** When true the trigger shows as just the icon (collapsed sidebar). */
  iconOnly?: boolean;
  className?: string;
}

export function CoachNotificationBell({ iconOnly, className }: CoachNotificationBellProps) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { items, unreadCount, markRead, markAllRead, updatesUnavailable, retryUpdates } = useCoachNotificationItems();

  const ariaLabel =
    unreadCount > 0
      ? `Notifications, ${unreadCount} unread`
      : "Notifications";

  const handleSelect = async (item: CoachNotificationItem) => {
    if (item.kind !== "session" && item.kind !== "development") await markRead(item);
    setOpen(false);
    navigate(item.route);
  };

  const handleMarkAllRead = async () => {
    setError("");
    try { await markAllRead(); } catch { setError("Some updates could not be marked reviewed. Refresh before trying again."); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={ariaLabel}
          title={ariaLabel}
          className={cn(
            "relative h-8 w-8 text-sidebar-foreground hover:bg-sidebar-accent",
            className,
          )}
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              className={cn(
                "absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold",
                "bg-primary text-primary-foreground",
              )}
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-80 p-0 bg-popover"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <span className="text-sm font-semibold text-foreground">Notifications</span>
          {items.some(item=>!item.read&&item.kind!=="session"&&item.kind!=="development") && (
            <button
              type="button"
              onClick={handleMarkAllRead}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Mark all read
            </button>
          )}
        </div>

        {/* Body */}
        <div className="py-1">
          {items.some(item=>item.kind==="session"||item.kind==="development") ? <p className="px-3 py-2 text-xs text-muted-foreground">Leader updates stay unread until you explicitly review them.</p> : null}
          {updatesUnavailable ? <p role="status" className="px-3 py-3 text-sm">Leader updates could not be checked. <button className="min-h-11 underline" onClick={()=>void retryUpdates()}>Retry leader updates</button></p> : null}
          {error ? <p role="alert" className="px-3 py-3 text-sm">{error}</p> : null}
          {items.length === 0 && !updatesUnavailable ? (
            <p className="px-3 py-4 text-sm text-muted-foreground text-center">
              You're all caught up.
            </p>
          ) : (
            <ul role="list" className="space-y-0.5 px-1 py-1">
              {items.map((item) => (
                <li key={item.id}>
                  <NotificationRow item={item} onSelect={handleSelect} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
